import React, { useState, useEffect } from 'react'
import {
  MDBModal,
  MDBModalHeader,
  MDBModalBody,
  MDBModalFooter,
  MDBInput,
  MDBBtn,
  MDBRow,
  MDBCol,
  MDBSelect,
  MDBBtnGroup,
} from 'mdbreact'
import { UITypeAhead } from './../../components/forms/form-fields'
import UserProfileService from './../../shared/services/UserProfile.service'
import NotificationService from './../../shared/services/Notification.service'
import CryptoJS from 'crypto-js'
import { toast } from 'react-toastify'
import AdminApi from './../../api/admin-api/admin-api'
import { commissionLevels } from '../../constants/commisionLevels'
import AuthService from './../../shared/services/Auth.service'

const EnrollmentLinkManager = ({ showModal, toggleModal }) => {
  const [enrollmentLink, setEnrollmentLink] = useState('')
  const [commissionList, setCommissionList] = useState([])
  const [commissionLevelList, setCommissionLevelList] = useState([])
  const [recruiterList, setRecruiterList] = useState([])
  const [uplineList, setUplineList] = useState([])
  const [uplineLeaderList, setUplineLeaderList] = useState([])
  const [loading, setLoading] = useState(false)
  const [selectedCommission, setSelectedCommission] = useState('')
  const [recruiterLoading, setRecruiterLoading] = useState(true)
  const [selectedRecruiter, setSelectedRecruiter] = useState(null)
  const [uplineLoading, setUplineLoading] = useState(true)
  const [selectedUpline, setSelectedUpline] = useState(null)
  const [emailAddress, setEmailAddress] = useState('')
  const [isTeamRecruiter, setTeamRecruiter] = useState(false)

  useEffect(() => {
    if (showModal) {
      initializeModal()
    }
  }, [showModal])

  const initializeModal = async () => {
    try {
      setLoading(true)
      setRecruiterLoading(true)

      await fetchCommissions()
      const u_login = UserProfileService.get('u_login')
      const currentUserId = UserProfileService.get('id')
      const is_team_recruiter = UserProfileService.get('can_recruit')
      let recruiterId, uplineId

      if (is_team_recruiter) {
        setTeamRecruiter(true)
        const uplineLeaders = await fetchUplineLeaders()
        setUplineList(uplineLeaders || [])
        setUplineLeaderList(uplineLeaders || [])
      } else {
        const downlineLeaders = await fetchDownlineLeaders()
        setRecruiterList(downlineLeaders || [])
        // Set default selected recruiter to current user
        const defaultRecruiter = downlineLeaders.find(
          (l) => l.id === currentUserId
        )
        if (defaultRecruiter && !is_team_recruiter) {
          setSelectedRecruiter(defaultRecruiter.id)
        }
        if (!is_team_recruiter) {
          recruiterId = CryptoJS.AES.encrypt(
            currentUserId.toString(),
            'usabg'
          ).toString()
        }
      }

      const generatedLink = enrollmentUrlGenerator(
        u_login,
        recruiterId,
        '',
        uplineId
      )
      setEnrollmentLink(generatedLink)
    } catch (error) {
      console.error('Error initializing modal:', error)
      toast.error('Failed to initialize enrollment link data')
    } finally {
      setLoading(false)
      setRecruiterLoading(false)
      setUplineLoading(false)
    }
  }

  const enrollmentUrlGenerator = (
    u_login,
    recruiterId = '',
    com_code = '',
    uplineId = ''
  ) => {
    let generatedLink = `${window.location.origin}/${u_login}/enroll`
    const params = []

    if (recruiterId) {
      params.push(`recruiterId=${recruiterId}`)
    }
    if (uplineId) {
      params.push(`uplineId=${uplineId}`)
    }
    if (com_code) {
      params.push(`com_code=${com_code}`)
    }

    if (params.length > 0) {
      generatedLink += `?${params.join('&')}`
    }

    return generatedLink
  }

  const copyToClipboard = () => {
    navigator.clipboard.writeText(enrollmentLink)
    toast.success('Link copied to clipboard')
  }

  const visitLink = () => {
    AuthService.logout().finally(() => {
      window.open(enrollmentLink)
    })
  }

  const sendEmail = () => {
    if (!emailAddress) {
      toast.error('Please enter an email address')
      return
    }

    NotificationService.email({
      email_id: 289,
      data: {
        email_to: emailAddress,
        email_cc: UserProfileService.get('u_email'),
        enrollment_link: enrollmentLink,
      },
    }).then(
      () => {
        toast.success(`Enrollment email sent to ${emailAddress}`)
      },
      (err) => {
        console.log(`Failed to send email. ${err}`)
      }
    )
  }

  const encryptValue = (value) => {
    if (!value) return ''
    return CryptoJS.AES.encrypt(value.toString(), 'usabg').toString()
  }

  const generateEnrollmentLink = (u_login, recruiterId, com_code, uplineId) => {
    return enrollmentUrlGenerator(
      u_login,
      encryptValue(recruiterId),
      encryptValue(com_code),
      encryptValue(uplineId)
    )
  }

  const updateEnrollmentLink = (recruiterId, com_code, uplineId = '') => {
    const u_login = UserProfileService.get('u_login')
    const generatedLink = generateEnrollmentLink(
      u_login,
      recruiterId,
      com_code,
      uplineId
    )
    setEnrollmentLink(generatedLink)
    toast.success('Enrollment link Updated')
  }

  const handleCommissionChange = (com_code) => {
    setSelectedCommission(com_code)

    if (!com_code) return false

    const is_team_recruiter = UserProfileService.get('can_recruit')

    const recruiterId = selectedRecruiter
      ? selectedRecruiter
      : !is_team_recruiter
      ? UserProfileService.get('id')
      : ''
    const uplineId = selectedUpline ? selectedUpline : ''
    updateEnrollmentLink(recruiterId, com_code, uplineId)
  }

  const handleRecruiterChange = (recruiterOption) => {
    if (!recruiterOption[0]) return false

    setSelectedRecruiter(recruiterOption[0])
    updateEnrollmentLink(recruiterOption[0], selectedCommission)
  }

  const handleUplineChange = (uplineOption) => {
    if (!uplineOption[0]) return false

    const upline = uplineList.find((item) => item.id == uplineOption[0])
    if (upline) {
      // Get commission_id from the found upline object (with null checks)
      const uplineCommissionId = upline?.commission_id

      // Define commission levels based on upline's commission_id
      const commission_levels = [
        commissionLevels.agent,
        commissionLevels.seniorBenefitsCounselor,
      ].concat(
        uplineCommissionId === 130 || uplineCommissionId === 213
          ? [commissionLevels.region, commissionLevels.district]
          : uplineCommissionId === 129
          ? [commissionLevels.district]
          : []
      )

      // Filter and map commission levels to options
      const options = commissionLevelList
        .filter((s) => commission_levels.includes(s.com_code))
        .map((s) => ({
          value: `${s.id}`,
          text: `${s.com_name} (${s.com_code})`,
        }))
      if (options) {
        setCommissionList(options || [])
      }
    }

    setSelectedUpline(uplineOption[0])
    updateEnrollmentLink('', selectedCommission, uplineOption[0])
  }

  const fetchUplineLeaders = async () => {
    try {
      setUplineLoading(true)
      const response = await AdminApi.getLimitedLeadersByUplineId(
        UserProfileService.get('u_upline_id')
      )
      let leaders = response?.data?.data || []

      return leaders.sort((a, b) =>
        `${[a.u_fname, a.u_lname].join(' ').trim()}`.localeCompare(
          `${[b.u_fname, b.u_lname].join(' ').trim()}`,
          'en'
        )
      )
    } catch (error) {
      console.error('Error fetching upline leaders:', error)
      toast.error('Failed to load upline list')
      return []
    }
  }

  const fetchDownlineLeaders = async () => {
    try {
      setRecruiterLoading(true)
      const response = await AdminApi.getLimitedDownlineLeadersId(
        UserProfileService.get('id')
      )
      let leaders = response?.data?.data || []

      const is_team_recruiter = UserProfileService.get('can_recruit')
      const current_user_id = UserProfileService.get('id')
      if (is_team_recruiter) {
        leaders = leaders.filter((leader) => leader.id !== current_user_id)
      }

      return leaders.sort((a, b) =>
        `${[a.u_fname, a.u_lname].join(' ').trim()}`.localeCompare(
          `${[b.u_fname, b.u_lname].join(' ').trim()}`,
          'en'
        )
      )
    } catch (error) {
      console.error('Error fetching downline leaders:', error)
      toast.error('Failed to load recruiter list')
      return []
    }
  }

  const fetchCommissions = async () => {
    try {
      const upline = (
        await AdminApi.getUserDataById(UserProfileService.get('u_upline_id'))
      )?.data
      let commissionLevelsList = []

      try {
        commissionLevelsList =
          (await AdminApi.getCommissionLevelList())?.data?.data || []
      } catch (ex) {
        console.error('Error fetching commission levels:', ex)
      }
      setCommissionLevelList(commissionLevelsList)
      upline.commission_id = upline?.data?.commission_id
      const commission_levels = [
        commissionLevels.agent,
        commissionLevels.seniorBenefitsCounselor,
      ].concat(
        upline?.commission_id === 130 || upline?.commission_id === 213
          ? [commissionLevels.region, commissionLevels.district]
          : upline?.commission_id === 129
          ? [commissionLevels.district]
          : []
      )

      const options = commissionLevelsList
        .filter((s) => commission_levels.includes(s.com_code))
        .map((s) => ({
          value: `${s.id}`,
          text: `${s.com_name} (${s.com_code})`,
        }))

      setCommissionList(options || [])
    } catch (error) {
      console.error('Error fetching commissions:', error)
      toast.error('Failed to load commission levels')
    }
  }

  return (
    <MDBModal
      size="lg"
      isOpen={showModal}
      toggle={toggleModal}
      className="AgentNavEnrollment"
    >
      <MDBModalHeader toggle={toggleModal}>
        Agent Enrollment Link
      </MDBModalHeader>
      <MDBModalBody>
        <MDBRow>
          <MDBCol lg="6">
            {!isTeamRecruiter && (
              <MDBSelect
                options={recruiterList.map((L) => ({
                  value: L.id,
                  text: [L.u_fname, L.u_lname].join(' ').trim(),
                  checked: L.id === selectedRecruiter,
                }))}
                value={selectedRecruiter || 'Select Recruiter'}
                selected={selectedRecruiter || 'Select Recruiter'}
                getValue={(value) => {
                  if (value[0] !== selectedRecruiter) {
                    handleRecruiterChange(value)
                  }
                }}
                label="Select Recruiter"
                disabled={recruiterLoading}
              />
            )}
            {isTeamRecruiter && (
              <MDBSelect
                options={uplineList.map((L) => ({
                  value: L.id,
                  text: [L.u_fname, L.u_lname].join(' ').trim(),
                  checked: L.id === selectedUpline,
                }))}
                value={selectedUpline || 'Select Upline'}
                selected={selectedUpline || 'Select Upline'}
                getValue={(value) => {
                  if (value[0] !== selectedUpline) {
                    handleUplineChange(value)
                  }
                }}
                label="Select Upline"
                disabled={uplineLoading}
              />
            )}
            {/* <UITypeAhead
              placeholder={
                recruiterLoading ? 'Loading recruiters...' : 'Select Recruiter'
              }
              onSelect={handleRecruiterChange}
              options={recruiterList.map((L) => ({
                value: L.id,
                text: [L.u_fname, L.u_lname].join(' ').trim(),
              }))}
              value={selectedRecruiter}
              disabled={recruiterLoading}
              searchable={true}
            /> */}
          </MDBCol>
          <MDBCol lg="6">
            <MDBSelect
              options={commissionList.map((item) => ({
                text: item.text,
                value: item.value,
                checked: item.value === selectedCommission,
              }))}
              value={selectedCommission || 'Select Commission Level'}
              selected={selectedCommission || 'Select Commission Level'}
              getValue={(value) => {
                if (value[0] !== selectedCommission) {
                  handleCommissionChange(value[0])
                }
              }}
              label="Commission Level"
              disabled={loading || commissionList.length === 0}
            />
          </MDBCol>
          <MDBCol lg="12">
            <div>
              <MDBInput
                type="text"
                value={enrollmentLink}
                readOnly
                className="form-control"
              />
            </div>
          </MDBCol>
          <MDBCol lg="12" className="mt-3">
            <MDBBtnGroup className="with-gaps">
              <MDBBtn color="danger" size="sm" onClick={copyToClipboard}>
                Copy Link
              </MDBBtn>
              <MDBBtn color="secondary" size="sm" onClick={visitLink}>
                Visit Link
              </MDBBtn>
              <div className="d-flex align-items-center">
                <MDBInput
                  label="Email Address"
                  value={emailAddress}
                  onChange={(e) => setEmailAddress(e.target.value)}
                  className="mr-2 flex-grow-1"
                />
                <MDBBtn color="primary" size="sm" onClick={sendEmail}>
                  Email Link
                </MDBBtn>
              </div>
            </MDBBtnGroup>
          </MDBCol>
        </MDBRow>
      </MDBModalBody>
      <MDBModalFooter>
        <MDBBtn className="btn-block" onClick={toggleModal}>
          Close
        </MDBBtn>
      </MDBModalFooter>
    </MDBModal>
  )
}

export default EnrollmentLinkManager
